'use client'

import React, { useState, useEffect } from 'react'
import { FileRejection } from 'react-dropzone'
import Image from 'next/image'
import { isEmpty } from 'lodash'
import { FileDropzone } from '.'
import { Upload } from 'flowbite-react-icons/outline'
import { MAX_4MB_FILE_SIZE } from '@/constants/file'

const DEFAULT_ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
}

interface AppDropzoneProps {
  initValue?: string[]
  id: string
  filePath: string
  helperText: string
  onSuccess: (value: string[]) => void
  onError?: (value: string) => void
  multiple?: boolean
  maxFileSize?: number
  acceptedFileTypes?: any
}

const AppDropzone: React.FC<AppDropzoneProps> = ({
  initValue,
  id,
  filePath,
  helperText,
  onSuccess,
  onError,
  multiple = false,
  maxFileSize = MAX_4MB_FILE_SIZE,
  acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
}) => {
  const [previews, setPreviews] = useState<string[]>(initValue || [])
  const [imgUrls, setImgUrls] = useState<string[]>(initValue || [])
  const [uploading, setUploading] = useState<boolean>(false)
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([])

  useEffect(() => {
    onSuccess(imgUrls)
  }, [imgUrls])

  async function uploadFileToS3(file: File) {
    const formData = new FormData()
    formData.append('path', filePath)
    formData.append('file', file as Blob)

    try {
      const response = await fetch('/api/s3/upload', {
        method: 'POST',
        body: formData,
      })

      return await response.json()
    } catch (error) {
      onError?.((error as Error).message)
      return ''
    }
  }

  const handlePreviews = (file: File) => {
    const preview = URL.createObjectURL(file)
    if (!multiple) {
      previews.forEach((preview) => URL.revokeObjectURL(preview))
      setPreviews([preview])
    } else {
      setPreviews((prev) => [...prev, preview])
    }
  }

  const onFileChange = async (
    files: File[],
    fileRejections: FileRejection[]
  ) => {
    setFileRejections(fileRejections)
    setUploading(true)

    const uploads = files.map(async (file) => {
      handlePreviews(file)

      const url = await uploadFileToS3(file)
      if (url) {
        setImgUrls((prev) => (multiple ? [...prev, url] : [url]))
      }
    })

    await Promise.all(uploads)
    setUploading(false)
  }

  const PreviewImg = ({ imgUrl }: { imgUrl: string }) => (
    <div className="relative w-full h-full">
      <Image
        src={imgUrl}
        fill
        style={{
          objectFit: 'contain',
        }}
        alt="Preview"
        className="w-full h-full object-cover rounded-lg"
        // Revoke data uri after image is loaded
        // onLoad={() => URL.revokeObjectURL(preview)}
      />
    </div>
  )

  const Placeholder = () => (
    <>
      <Upload className="mb-2 h-6 w-6 text-gray-400" />
      <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
        <span className="font-semibold">Click to upload</span> or drag and drop
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
    </>
  )

  // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
  // useEffect(() => {
  //   return () => URL.revokeObjectURL(preview)
  // })

  return (
    <>
      <FileDropzone
        onChange={onFileChange}
        accept={acceptedFileTypes}
        multiple={multiple}
        maxSize={maxFileSize}
        disabled={uploading}
        loading={uploading}
      >
        {isEmpty(previews) ? (
          <Placeholder />
        ) : (
          <div
            className={`${previews.length > 1 ? `grid gap-4 grid-cols-3 grid-rows-${previews.length / 3 + 1}` : ''} w-full h-full p-2`}
          >
            {previews.length > 0 &&
              previews.map((preview, index) => (
                <PreviewImg key={`preview_${index}`} imgUrl={preview} />
              ))}
          </div>
        )}
      </FileDropzone>
      {fileRejections.length > 0 && (
        <span className="mt-2 text-red-600 text-sm">
          {fileRejections[0].errors
            .map((error) => error.message)
            .join('&nbsp;')}
        </span>
      )}
    </>
  )
}

export default AppDropzone
